import { inject, injectable } from 'inversify';
import { CallbackManager } from '@/engine/Utilities/CallbackManager';
import type { CallbackSubscription } from '@/engine/Utilities/CallbackManager';
import type { CaseDataProvider } from '../../CaseDataProvider';
import type {
  ContrastFunction,
  MedicalImage,
  MinMax,
  OnPositionChangedListener,
  OnOrientationChangedListener,
  OnErrorListener,
  OnLoadingProgressListener,
  OnReadyStateChangedListener,
  SlicePositionInfo,
  SliceOrientationInfo,
  OnVisualizationChangedListener,
  SliceVisualizationInfo,
  OnSynchronizationPointChangedListener
} from './ImageStackController';
import { ImageStackController, middleIndexSlicesInitializer } from './ImageStackController';
import type { IRequestSliceStrategiesProvider, SliceOrientationsConfig, IImagesInfo } from './interfaces';
import { SliceType } from './interfaces';
import { ImagesMode, SlicerMode } from './interfaces';
import { TYPES as TYPES1 } from './iocTypes';
import { TYPES as TYPES2 } from '@/engine/ioc';
import type * as THREE from 'three';
import type { Vector3 } from 'three';
import { ImagesInfo } from './ImagesInfo';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import { computed, makeObservable, observable, action } from 'mobx';
import { IDisposable, safeResource } from '@/engine/Utilities/IDisposable';
import type { CasePhaseData } from '../../CasePhaseData/CasePhaseData.ts';
import type { INotificationsCenter } from '../../interfaces.ts';
import { XRayEffect } from './ImageStack/ImageStack.ts';
import { AutoDisposer } from '@/engine/Utilities/AutoDisposer.ts';

export interface IRequestSliceStrategy {
  requestSlice: (sliceType: SliceType, index: number) => void;
  requestSliceByPoint: (sliceType: SliceType, coplanarPoint: Vector3) => void;
  syncSliceWithPlane: (
    slice: SliceType,
    origin: Vector3,
    normal: Vector3,
    orientationY?: Vector3,
    excludedSlice?: SliceType,
    isDeltaTransformation?: boolean
  ) => void;
  dispose: () => void;
}

export type OnRoiPointChangedListener = (roiPoint: THREE.Vector3, sliceType?: SliceType) => void;

@injectable()
export class ImagesService {
  private readonly _caseDataProvider: CaseDataProvider;

  private readonly _requestSliceStrategiesProvider: IRequestSliceStrategiesProvider;

  private readonly _casePhaseData: CasePhaseData;

  private _imageStackController?: ImageStackController;

  private _imageKind?: string;

  private _polylineInfo?: { points: Array<Vector3>; normals: Array<Vector3>; slice: SliceType };

  private readonly _imageStackConfigs = new Map<
    string,
    { dsc: MedicalImage; sliceOrientations: SliceOrientationsConfig }
  >();

  private readonly _imageStackControllers = new Map<string, ImageStackController>();

  private readonly _imageOrientationInfos = new Map<SliceType, ImagesInfo>();

  private readonly _imageStackSubscriptions = new DisposablesBasket();

  private readonly _onSliceOrientationChangedListeners = new Map<
    SliceType,
    CallbackManager<OnOrientationChangedListener>
  >();

  private readonly _onSlicePositionChangedListeners = new Map<SliceType, CallbackManager<OnPositionChangedListener>>();

  private readonly _onSliceVisualizationChangedListeners = new Map<
    SliceType,
    CallbackManager<OnVisualizationChangedListener>
  >();

  private readonly _onSynchronizationPointChangedListeners =
    new CallbackManager<OnSynchronizationPointChangedListener>();

  private readonly _onRoiPointRequestedListeners = new CallbackManager<OnRoiPointChangedListener>();

  private readonly _onReadyStateChangedListeners = new CallbackManager<OnReadyStateChangedListener>();

  private readonly _onErrorListeners = new CallbackManager<OnErrorListener>();

  private readonly _onLoadingProgressListeners = new CallbackManager<OnLoadingProgressListener>();

  private readonly _notificationsCenter: INotificationsCenter;

  private _requestSliceStrategy?: IRequestSliceStrategy;

  private _areSlicesModificationsEnabled = true;

  @observable private _imagesMode = ImagesMode.MPR;

  @observable private _activeXRaySlicer: [SliceType, SlicerMode] | undefined = undefined;

  @observable private _isImagesLoadingCompleted = false;

  @observable private _isCineloopAvailable = false;

  public constructor(
    @inject(TYPES2.CaseDataProvider) caseDataProvider: CaseDataProvider,
    @inject(TYPES1.IRequestSliceStrategiesProvider) requestSliceStrategiesProvider: IRequestSliceStrategiesProvider,
    @inject(TYPES2.CasePhaseData) casePhaseData: CasePhaseData,
    @inject(TYPES2.NotificationsCenter) notificationsCenter: INotificationsCenter
  ) {
    this._caseDataProvider = caseDataProvider;
    this._requestSliceStrategiesProvider = requestSliceStrategiesProvider;
    this._casePhaseData = casePhaseData;
    this._notificationsCenter = notificationsCenter;
    makeObservable(this);
  }

  private async _getImagesContrastFunction(guid: string): Promise<ContrastFunction> {
    const header = await this._caseDataProvider.getCaseHeader();
    for (let i = 0, sz = header.imagesLength(); i < sz; ++i) {
      const image = header.images(i);
      if (image == null) throw new Error('Image expected to be not null');
      if (image.guid() === guid) {
        const contrast = image.contrast();
        if (contrast == null) throw new EvalError('Contrast info missing');
        const lb = contrast.lowerBound();
        const ub = contrast.upperBound();
        if (lb === null || ub === null) throw new EvalError('Contrast info missing');
        return {
          lowerBound: { from: lb.from(), to: lb.to() },
          upperBound: { from: ub.from(), to: ub.to() }
        };
      }
    }
    throw new EvalError('Image info missing');
  }

  public async loadImagesConfig(sliceOrientations: SliceOrientationsConfig): Promise<void> {
    sliceOrientations.forEach((_, sliceType) => this._imageOrientationInfos.set(sliceType, new ImagesInfo()));

    const kinds = this._casePhaseData.getRegisteredKinds();
    if (kinds.length == 0) throw new Error('Image Phases are expected to be registered so far');
    for (const kind of kinds) {
      const ids = this._casePhaseData.getAvailablePhases(kind);
      if (ids.length == 0) throw new Error('Phases list is expect to be not empty');
      const contrast = await this._getImagesContrastFunction(ids[0]); // [chebotarev]: we are taking very first image contrast in case of multistack
      this._imageStackConfigs.set(kind, {
        dsc: { ids: ids, contrast: contrast },
        sliceOrientations: sliceOrientations
      });
    }
  }

  private _getImageStackController(kind: string): ImageStackController {
    let isc = this._imageStackControllers.get(kind);
    if (!isc) {
      const config = this._imageStackConfigs.get(kind);
      if (!config) throw new Error('Unknown ImageStack');
      isc = new ImageStackController(
        config.dsc,
        this._caseDataProvider.getShortLivingCaseBlobUrlResolver(),
        config.sliceOrientations,
        middleIndexSlicesInitializer,
        this._notificationsCenter
      );
      this._imageStackControllers.set(kind, isc);
    }
    return isc;
  }

  @action
  public async loadImagesAsync(kind: string): Promise<void> {
    if (this._imageKind !== kind) {
      this._updateIsImagesLoadingCompleted(false);
      this._isCineloopAvailable = false;
      this._imageStackController?.suspendImages();
      this._imageStackSubscriptions.dispose();
      this._requestSliceStrategy?.dispose();
      this._imageKind = kind;
      const isc = this._getImageStackController(kind);
      isc.getRegisteredSliceTypes().forEach((sliceType) => {
        const info = this._imageOrientationInfos.get(sliceType);
        info?.setImageMode(this._imagesMode);
        if (!info) throw new Error('Unknown slice type');
        this._imageStackSubscriptions.add(
          isc.addOnOrientationChangedListener(sliceType, (orientationInfo) => {
            info.setFromSliceOrientation(orientationInfo);
            this._getSliceOrientationChangedListener(sliceType).invoke(orientationInfo);
          })
        );
        this._imageStackSubscriptions.add(
          isc.addOnPositionChangedListener(sliceType, (positionInfo) => {
            info.setFromSlicePosition(positionInfo);
            this._getSlicePositionChangedListener(sliceType).invoke(positionInfo);
          })
        );
        this._imageStackSubscriptions.add(
          isc.addOnVisualizationChangedListener(sliceType, (visualizationInfo) => {
            info.setFromSliceVisualization(visualizationInfo);
            this._getSliceVisualizationChangedListener(sliceType).invoke(visualizationInfo);
          })
        );
        this._imageStackSubscriptions.add(
          isc.addOnSynchronizationPointChangedListener((synchronizationPoint) => {
            this._onSynchronizationPointChangedListeners.invoke(synchronizationPoint);
          })
        );
        this._imageStackSubscriptions.add(
          isc.addOnReadyStateChangedListener((isReady) => {
            info.setReadyState(isReady);
            this._onReadyStateChangedListeners.invoke(isReady);
          })
        );
        this._imageStackSubscriptions.add(
          isc.addOnLoadingProgressListener((isReduced, framesLoaded, framesTotal) => {
            info.setImageLoadingProgress(isReduced, framesLoaded, framesTotal);
            this._onLoadingProgressListeners.invoke(isReduced, framesLoaded, framesTotal);
          })
        );
        this._imageStackSubscriptions.add(
          isc.addOnErrorOccurredListener(() => {
            this._onErrorListeners.invoke();
          })
        );
      });
      this._imageStackController = isc;
      this._requestSliceStrategy = this._requestSliceStrategiesProvider.getStrategy(
        this._imagesMode,
        this._imageStackController,
        this._imageOrientationInfos
      );
      try {
        const id = this._casePhaseData.getActivePhaseId(kind);
        await this._imageStackController.loadImagesAsync(id);
        this._updateIsImagesLoadingCompleted();
        this._isCineloopAvailable = this._imageStackController.isCineloopAvailable();
      } catch (e: unknown) {
        if (e instanceof Error) {
          throw e;
        } else {
          console.warn('Images loading interrupted');
        }
      }
    }
  }

  public async switchImagesAsync(guid: string): Promise<void> {
    if (!this._imageStackController) {
      throw new Error('Not initialized');
    }
    await this._imageStackController.switchImagesAsync(guid);
  }

  public getImageSliceOrientationInfo(sliceType: SliceType): IImagesInfo {
    const imageInfo: ImagesInfo | undefined = this._imageOrientationInfos.get(sliceType);
    if (!imageInfo) throw new Error(`No image info for ${sliceType} slice type`);
    return imageInfo;
  }

  public getRegisteredSliceTypes(): Array<SliceType> {
    if (!this._imageStackController) {
      throw new Error('Not initialized');
    }
    return this._imageStackController.getRegisteredSliceTypes();
  }

  public getSlicesOrientationsMementos(useInitialOrientation = false): Map<SliceType, THREE.Matrix4> {
    if (!this._imageStackController) {
      throw new Error('Not initialized');
    }
    return this._imageStackController.getSlicesOrientationsMementos(useInitialOrientation);
  }

  public setSlicesOrientationsMementos(
    orientationCSs: Map<SliceType, THREE.Matrix4>,
    origin?: THREE.Vector3,
    deltaTransformations?: Map<SliceType, THREE.Matrix4>
  ): void {
    if (!this._imageStackController) {
      throw new Error('Not initialized');
    }
    if (!this._areSlicesModificationsEnabled) {
      return;
    }
    this._imageStackController.setSlicesOrientationsMementos(orientationCSs, origin, deltaTransformations);
  }

  public swapSlices(first: SliceType, second: SliceType): void {
    if (!this._imageStackController) {
      throw new Error('Not initialized');
    }
    if (!this._areSlicesModificationsEnabled) {
      return;
    }
    if (first == second) {
      return;
    }
    const orientations = this._imageStackController.getSlicesOrientationsMementos();
    const firstOrientation = orientations.get(first);
    const secondOrientation = orientations.get(second);
    if (!firstOrientation || !secondOrientation) {
      throw new Error('Not initialized');
    }
    orientations.set(first, secondOrientation);
    orientations.set(second, firstOrientation);
    this._imageStackController.setSlicesOrientationsMementos(orientations);
  }

  public getSlicesIntersectionPoint(): THREE.Vector3 {
    if (!this._imageStackController) {
      throw new Error('Not initialized');
    }
    const pt = this._imageStackController.getSlicesIntersectionPoint();
    if (!pt) {
      throw new Error('Slices are not synchronized');
    }
    return pt;
  }

  public getContrast(): MinMax | undefined {
    if (!this._imageStackController) {
      throw new Error('Not initialized');
    }
    return this._imageStackController.getContrast();
  }

  public getGVMinMax(): MinMax | undefined {
    if (!this._imageStackController) {
      throw new Error('Not initialized');
    }
    return this._imageStackController.getGVMinMax();
  }

  public getPolylineInfo():
    | {
        points: Array<Vector3>;
        normals: Array<THREE.Vector3>;
        slice: SliceType;
      }
    | undefined {
    return this._polylineInfo;
  }

  public setContrast(contrastWindow: MinMax): void {
    if (!this._imageStackController) {
      throw new Error('Not initialized');
    }
    return this._imageStackController.setContrast(contrastWindow);
  }

  public resetContrast(): void {
    this._imageStackController?.resetContrast();
  }

  public applyMpr(
    slicesOrientationsMementos: Map<SliceType, THREE.Matrix4>,
    sliceType: SliceType,
    origin: THREE.Vector3,
    rotationMatrix?: THREE.Matrix4
  ): void {
    if (!this._imageStackController) {
      throw new Error('Not initialized');
    }
    if (!this._areSlicesModificationsEnabled) {
      return;
    }
    this._imageStackController.applyMpr(slicesOrientationsMementos, sliceType, origin, rotationMatrix);
  }

  public resetMpr(isDeltaTransformation = false, resetPosition = false): void {
    if (!this._imageStackController) {
      throw new Error('Not initialized');
    }
    if (!this._areSlicesModificationsEnabled) {
      return;
    }
    this._imageStackController.resetMpr(isDeltaTransformation, resetPosition);
  }

  public setPolylineInfo(slice: SliceType, points: Array<Vector3>, normals: Array<Vector3>): boolean {
    this._polylineInfo = { points, normals, slice };

    return this._imageOrientationInfos.get(slice)?.setPolylineInfo(points, normals) ?? false;
  }

  @action
  public setImagesMode(mode: ImagesMode): void {
    if (this._imagesMode === mode) return;
    this._imagesMode = mode;
    if (!this._imageStackController) {
      throw new Error('Not initialized');
    }
    this._requestSliceStrategy?.dispose();
    this._requestSliceStrategy = this._requestSliceStrategiesProvider.getStrategy(
      this._imagesMode,
      this._imageStackController,
      this._imageOrientationInfos
    );
    this._imageOrientationInfos.forEach((info) => {
      info.setImageMode(mode);
    });
  }

  @action
  public setSlicerMode(sliceType: SliceType, slicerMode: SlicerMode, sliceThickness?: number): void {
    if (!this._imageStackController) {
      throw new Error('Not initialized');
    }
    for (const _sliceType of this._imageStackController.getRegisteredSliceTypes()) {
      if (_sliceType !== sliceType && this._imageStackController.getXRaySlicerParams(_sliceType) !== undefined) {
        this._imageStackController.setXRaySlicerParams(_sliceType, undefined);
      }
    }
    switch (slicerMode) {
      case SlicerMode.SINGLE: {
        if (this._imageStackController.getXRaySlicerParams(sliceType)) {
          this._imageStackController.setXRaySlicerParams(sliceType, undefined);
        }
        this._activeXRaySlicer = undefined;
        break;
      }
      case SlicerMode.AIP: {
        this._imageStackController.setXRaySlicerParams(sliceType, {
          xRayEffect: XRayEffect.Addition,
          xRaySliceThickness: sliceThickness ?? 10,
          xRayReductionFactor: 0.7,
          xRayMaxSlicesCount: 25
        });
        this._activeXRaySlicer = [sliceType, SlicerMode.AIP];
        break;
      }
      case SlicerMode.FLUORO: {
        this._imageStackController.setXRaySlicerParams(sliceType, {
          xRayEffect: XRayEffect.Subtraction,
          xRayRescale: 1.0,
          xRayReductionFactor: 0.25
        });
        this._activeXRaySlicer = [sliceType, SlicerMode.FLUORO];
        break;
      }
    }
  }

  public get imageOrientationInfos(): Map<SliceType, ImagesInfo> {
    return this._imageOrientationInfos;
  }

  public syncImages(point3D: THREE.Vector3): void {
    if (!this._imageStackController) {
      throw new Error('Not initialized');
    }
    if (!this._areSlicesModificationsEnabled) {
      return;
    }
    if (this._imageStackController.isReady()) {
      this._imageStackController.synchronizeImages(point3D);
    } else {
      console.warn('Action ignored Images are still loading');
    }
  }

  private _getSliceOrientationChangedListener(sliceType: SliceType): CallbackManager<OnOrientationChangedListener> {
    let listener = this._onSliceOrientationChangedListeners.get(sliceType);
    if (!listener) {
      listener = new CallbackManager();
      this._onSliceOrientationChangedListeners.set(sliceType, listener);
    }
    return listener;
  }

  private _getSlicePositionChangedListener(sliceType: SliceType): CallbackManager<OnPositionChangedListener> {
    let listener = this._onSlicePositionChangedListeners.get(sliceType);
    if (!listener) {
      listener = new CallbackManager();
      this._onSlicePositionChangedListeners.set(sliceType, listener);
    }
    return listener;
  }

  private _getSliceVisualizationChangedListener(sliceType: SliceType): CallbackManager<OnVisualizationChangedListener> {
    let listener = this._onSliceVisualizationChangedListeners.get(sliceType);
    if (!listener) {
      listener = new CallbackManager();
      this._onSliceVisualizationChangedListeners.set(sliceType, listener);
    }
    return listener;
  }

  public subscribeToSliceOrientationChange(
    sliceType: SliceType,
    handler: OnOrientationChangedListener
  ): CallbackSubscription {
    return safeResource(this._getSliceOrientationChangedListener(sliceType).subscribe(handler), () => {
      const orientationInfo = this.getSliceOrientationInfo(sliceType);
      if (orientationInfo) handler(orientationInfo);
    });
  }

  public subscribeToSlicePositionChange(
    sliceType: SliceType,
    handler: OnPositionChangedListener
  ): CallbackSubscription {
    return safeResource(this._getSlicePositionChangedListener(sliceType).subscribe(handler), () => {
      const positionInfo = this.getSlicePositionInfo(sliceType);
      if (positionInfo) handler(positionInfo);
    });
  }

  public subscribeToSliceVisualizationChange(
    sliceType: SliceType,
    handler: OnVisualizationChangedListener
  ): CallbackSubscription {
    return safeResource(this._getSliceVisualizationChangedListener(sliceType).subscribe(handler), () => {
      const visualizationInfo = this.getSliceVisualizationInfo(sliceType);
      if (visualizationInfo) handler(visualizationInfo);
    });
  }

  public subscribeToSynchronizationPointChanged(handler: OnSynchronizationPointChangedListener): CallbackSubscription {
    return safeResource(this._onSynchronizationPointChangedListeners.subscribe(handler), () => {
      const pt = this._imageStackController?.getSlicesIntersectionPoint();
      if (pt) {
        handler(pt);
      }
    });
  }

  public subscribeToRoiPointRequested(handler: OnRoiPointChangedListener): CallbackSubscription {
    return this._onRoiPointRequestedListeners.subscribe(handler);
  }

  public subscribeToReadyStateChange(handler: OnReadyStateChangedListener): CallbackSubscription {
    return safeResource(this._onReadyStateChangedListeners.subscribe(handler), () => {
      handler(this.isReady());
    });
  }

  public subscribeToLoadingProgressListener(handler: OnLoadingProgressListener): CallbackSubscription {
    return this._onLoadingProgressListeners.subscribe(handler);
  }

  public subscribeToErrorListener(handler: OnErrorListener): CallbackSubscription {
    return this._onErrorListeners.subscribe(handler);
  }

  public requestSlice(sliceType: SliceType, index: number): void {
    if (!this._imageStackController || !this._requestSliceStrategy) {
      throw new Error('Not initialized');
    }
    if (this._imageStackController.isReady()) {
      this._requestSliceStrategy.requestSlice(sliceType, index);
    } else {
      console.warn('Action ignored Images are still loading');
    }
  }

  public requestSliceByPoint(sliceType: SliceType, coplanarPoint: Vector3): void {
    if (!this._imageStackController || !this._requestSliceStrategy) {
      throw new Error('Not initialized');
    }
    if (!this._areSlicesModificationsEnabled) {
      return;
    }
    if (this._imageStackController.isReady()) {
      this._requestSliceStrategy.requestSliceByPoint(sliceType, coplanarPoint);
    } else {
      console.warn('Action ignored Images are still loading');
    }
  }

  public getSliceOrientationInfo(sliceType: SliceType): SliceOrientationInfo | undefined {
    return this._imageStackController?.getSliceOrientationInfo(sliceType);
  }

  public getSlicePositionInfo(sliceType: SliceType): SlicePositionInfo | undefined {
    return this._imageStackController?.getSlicePositionInfo(sliceType);
  }

  public getSliceVisualizationInfo(sliceType: SliceType): SliceVisualizationInfo | undefined {
    return this._imageStackController?.getSliceVisualizationInfo(sliceType);
  }

  public isReady(): boolean {
    return this._imageStackController?.isReady() === true;
  }

  public resolveClosestOrientation(
    vectorZ: THREE.Vector3,
    useInitialOrientation = false
  ): {
    sliceType: SliceType;
    adjustedVectorZ: THREE.Vector3;
    adjustedVectorY?: THREE.Vector3;
  } {
    if (!this._imageStackController) {
      throw new Error('Not initialized');
    }
    return this._imageStackController.resolveClosestOrientation(vectorZ, useInitialOrientation);
  }

  public syncSliceWithPlane(
    sliceType: SliceType,
    origin: Vector3,
    normal: Vector3,
    orientationY?: Vector3,
    excludedSlice?: SliceType,
    isDeltaTransformation = false
  ): void {
    if (!this._requestSliceStrategy) {
      throw new Error('Not initialized');
    }
    if (!this._areSlicesModificationsEnabled) {
      return;
    }
    return this._requestSliceStrategy.syncSliceWithPlane(
      sliceType,
      origin,
      normal,
      orientationY,
      excludedSlice,
      isDeltaTransformation
    );
  }

  public setSliceOrientation(sliceType: SliceType, vecDir: Vector3, vecTop: Vector3): void {
    if (!this._areSlicesModificationsEnabled) {
      return;
    }
    const resolved = this.resolveClosestOrientation(vecDir, true);
    this.syncSliceWithPlane(
      resolved.sliceType,
      this.getSlicesIntersectionPoint(),
      resolved.sliceType !== SliceType.AXIAL ? vecDir.clone().negate() : vecDir,
      vecTop
    );
    this.swapSlices(resolved.sliceType, sliceType);
  }

  public setImageSliceRotation(sliceType: SliceType, complimentarySlice: SliceType, rotationValue: number): void {
    if (!this._areSlicesModificationsEnabled) {
      return;
    }
    return this._imageStackController?.setMprSliceRotation(sliceType, complimentarySlice, rotationValue);
  }

  public requestRoiPoint(roiPoint: THREE.Vector3, sliceType?: SliceType): void {
    if (!this._areSlicesModificationsEnabled) {
      return;
    }
    this._onRoiPointRequestedListeners.invoke(roiPoint, sliceType);
  }

  public acquireSlicesModificationsLock(): IDisposable {
    this._areSlicesModificationsEnabled = false;
    return new AutoDisposer(() => {
      this._areSlicesModificationsEnabled = true;
    });
  }

  @action
  private _updateIsImagesLoadingCompleted(override?: boolean): void {
    if (override !== undefined) {
      this._isImagesLoadingCompleted = override;
    } else {
      this._isImagesLoadingCompleted = Array.from(this._imageStackControllers.values()).every(
        (isc: ImageStackController): boolean => (this._isCineloopAvailable ? isc.isCompleted() : isc.isReady())
      );
    }
  }

  @computed
  public get imagesMode(): ImagesMode {
    return this._imagesMode;
  }

  @computed
  public get activeXRaySlicer(): [SliceType, SlicerMode] | undefined {
    return this._activeXRaySlicer;
  }

  @computed
  public get isImagesLoadingCompleted(): boolean {
    return this._isImagesLoadingCompleted;
  }

  @computed
  public get isCineloopAvailable(): boolean {
    return this._isCineloopAvailable;
  }
}
